import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import useSiteCopySelector from 'components/useSiteCopySelector';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

const useStyles = makeStyles({
  root: {
    '& input': {
      height: '1.2em',
    },
    '& select': {
      height: '1.2em',
      lineHeight: '1.35',
    },
  },
  padded: {
    '& input': {
      padding: '16px 14px',
    },
    '& select': {
      padding: '16px 14px',
    },
  },
  fontSize: {
    fontSize: '1.063rem',
  },
  placeholderColor: {
    color: '#999999',
  },
  shrinkColor: {
    color: '#4B4B4B',
  },
  labelCheck: {
    fontSize: '0.6875rem',
    width: 27,
    lineHeight: 'unset',
  },
  marginNone: {
    margin: 0,
    padding: 0,
  },
  checkboxApply: {
    padding: '0',
    paddingRight: 5,
  },
  helperText: {
    fontWeight: '400',
    fontSize: '0.75rem',
  },
});

function PasswordTextField({
  value,
  onChange,
  hideVisibilityToggle,
  plain,
  required = false,
  error,
  label,
  autoComplete = 'off',
}) {
  const classes = useStyles();
  const [siteCopy] = useSiteCopySelector(['sign-up']);
  const [isVisible, setVisible] = useState(false);

  const handleVisibilityChange = () => setVisible(!isVisible);

  return (
    <TextField
      id="password"
      name="password"
      label={label || _get(siteCopy, 'pageCopy.passwordPlaceholder')}
      helperText={error || null}
      error={!!error}
      classes={{
        root: _classNames(classes.root, { [classes.padded]: !plain }),
      }}
      InputProps={{
        classes: {
          root: classes.fontSize,
        },
        endAdornment: !hideVisibilityToggle ? (
          <InputAdornment position="end">
            <FormControlLabel
              classes={{
                label: classes.labelCheck,
                root: classes.marginNone,
              }}
              control={
                <Checkbox
                  id="showNewPassword"
                  disableRipple
                  classes={{ root: classes.checkboxApply }}
                  checkedIcon={
                    <CheckBoxIcon color="primary" style={{ fontSize: 14 }} />
                  }
                  icon={<CheckBoxOutlineBlankIcon style={{ fontSize: 14 }} />}
                  checked={isVisible}
                  onChange={handleVisibilityChange}
                />
              }
              label={
                isVisible
                  ? _get(siteCopy, 'pageCopy.passwordVisibilityHide')
                  : _get(siteCopy, 'pageCopy.passwordVisibilityShow')
              }
            />
          </InputAdornment>
        ) : null,
      }}
      InputLabelProps={{
        classes: {
          root: _classNames(classes.fontSize, classes.placeholderColor),
          shrink: classes.shrinkColor,
        },
      }}
      FormHelperTextProps={{
        classes: {
          root: classes.helperText,
        },
      }}
      onChange={onChange}
      value={value}
      fullWidth
      type={isVisible ? 'text' : 'password'}
      variant={plain ? 'standard' : 'outlined'}
      autoComplete={autoComplete}
      required={required}
    />
  );
}

PasswordTextField.propTypes = {
  error: PropTypes.string,
  hideVisibilityToggle: PropTypes.bool,
  label: PropTypes.string,
  plain: PropTypes.bool,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default PasswordTextField;
